import React from 'react';

export type FmChipVariant = 'brand' | 'info' | 'warn' | 'success' | 'mute';

export interface FmChipProps extends React.HTMLAttributes<HTMLElement> {
  as?: 'span' | 'button';
  variant?: FmChipVariant;
  leadingIcon?: React.ReactNode;
  dotColor?: string;
  /**
   * Marks the chip as an unresolved/default value: dashed border + reduced
   * opacity. Owned by the primitive so route code never inline-styles the
   * "this value is a guess" treatment.
   */
  uncertain?: boolean;
  /**
   * Allow the chip to hold wrapping, multi-line copy (e.g. a full annotation
   * sentence) instead of a single-line micro-badge. Switches the fixed pill
   * height to a min-height with vertical padding so long text never overflows
   * the pill or collides with neighbouring chips. Owned by the primitive so
   * route code never inline-styles the wrapping treatment.
   */
  multiline?: boolean;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  'data-testid'?: string;
  children: React.ReactNode;
}

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

/**
 * The chip's visible text, for the `title` a truncated single-line chip needs so
 * its full string stays reachable.
 *
 * KNOWN LIMIT: a child element is read through its `children`. A component that
 * renders words WITHOUT taking children (`<StatusWord />`) is indistinguishable
 * from an icon by its props, so it contributes nothing and the title would be
 * partial. Chips in this codebase pair text with icons, which is the case this
 * serves; a chip whose text comes from such a component should pass its own
 * `title`.
 */
function plainTextContent(node: React.ReactNode): string | undefined {
  const parts = React.Children.toArray(node);
  if (!parts.length) return undefined;
  const pieces: string[] = [];
  for (const child of parts) {
    if (typeof child === 'string' || typeof child === 'number') {
      pieces.push(String(child));
      continue;
    }
    if (React.isValidElement<{ children?: React.ReactNode; 'aria-hidden'?: unknown }>(child)) {
      /* Emptiness, not `aria-hidden`, decides. An aria-hidden element carrying
         VISIBLE text is still on screen, so skipping it produced a silently
         PARTIAL title — the thing this function refuses to do. Read every child;
         one with nothing in it contributes nothing, and one whose content cannot
         be read at all still yields no title. */
      const kids = child.props.children;
      /* An element with nothing to read contributes nothing — `<Icon />`,
         `<span />`, `{null}`. The earlier version tested only `null`/boolean and
         so let a childless `<span />` discard the entire title, which is the
         regression this contract exists to prevent (review round 4). */
      if (kids === undefined || kids === null || kids === '' || typeof kids === 'boolean') continue;
      const nested = plainTextContent(kids);
      /* A child that carries text this cannot read WOULD make the title partial,
         and a tooltip that silently drops half the chip is worse than none. */
      if (nested === undefined) return undefined;
      pieces.push(nested);
      continue;
    }
    return undefined;
  }
  const text = pieces.join('').trim();
  return text.length ? text : undefined;
}

function hasTextContent(node: React.ReactNode): boolean {
  return React.Children.toArray(node).some((child) => {
    if (typeof child === 'string') return child.trim().length > 0;
    if (typeof child === 'number') return true;
    if (React.isValidElement<{ children?: React.ReactNode }>(child)) {
      return hasTextContent(child.props.children);
    }
    return false;
  });
}

/** Compact status pill used for semantic metadata and micro-badges. */
export const FmChip: React.FC<FmChipProps> = ({
  as = 'span',
  variant = 'mute',
  leadingIcon,
  dotColor,
  uncertain,
  multiline,
  className,
  disabled,
  type = 'button',
  children,
  ...rest
}) => {
  const hideLeadingIcon = Boolean(
    hasTextContent(children) || rest['aria-label'] || rest['aria-labelledby'],
  );
  /* A single-line chip truncates rather than clipping, so the full string has to
     stay reachable. A multiline chip shows everything and needs no title. */
  const truncationTitle = multiline ? undefined : plainTextContent(children);
  const commonProps = {
    className: cx(
      'chip',
      `chip--${variant}`,
      uncertain && 'chip--uncertain',
      multiline && 'chip--multiline',
      className,
    ),
    ...rest,
    title: rest.title ?? truncationTitle,
  };
  const content = (
    <>
      {leadingIcon ? (
        <span className="chip__icon" aria-hidden={hideLeadingIcon || undefined}>
          {leadingIcon}
        </span>
      ) : null}
      {dotColor ? <span className="dot" style={{ background: dotColor }} aria-hidden="true" /> : null}
      {/* The label owns the truncation. `text-overflow` acts on a block
          container's inline content, and the chip itself is a flex container —
          declaring ellipsis there would silently do nothing and the chip would
          clip. */}
      <span className="chip__label">{children}</span>
    </>
  );

  if (as === 'button') {
    return (
      <button {...commonProps} type={type} disabled={disabled}>
        {content}
      </button>
    );
  }

  return <span {...commonProps}>{content}</span>;
};

export default FmChip;
